import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'primereact/tag';
import { Sidebar } from 'primereact/sidebar';
import { Toast } from 'primereact/toast';
import { SortOrder } from 'primereact/api';
import { useUsers } from '../hooks/useUsers';
import UserForm from '../components/UserForm';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { TableWithHeader, TableColumn } from '../../common';
import { User, CreateUserRequest, UpdateUserRequest } from '../types/users.types';
import { UserRole } from '../../auth/types/auth.types';
import { RoleService } from '../../layouts/services/role.service';
import { AlcanceMetasSection } from '../../metas-colaborador/components/AlcanceMetasSection';
import { MetasColaboradorService } from '../../metas-colaborador/services/metasColaborador.service';
import { StorageService } from '../../auth/services/storage.service';
import { PerfilUsuarioSection } from '../../perfiles/components/PerfilUsuarioSection';
import { CerrarSesionesDialog } from '../components/CerrarSesionesDialog';
import { BajaDialog } from '../components/BajaDialog';
import { tiendasParam } from '../../responsables/utils/responsables';
import {
  AlcanceMetasMin,
  admitePerfil,
  DIAS_SIN_USO,
  esCuentaCompartida,
  etiquetaUltimoAcceso,
  sinUso,
  textoAlcances,
} from '../utils/sesiones';

const fechaHoraMx = (v: string | Date) =>
  new Intl.DateTimeFormat('es-MX', { timeZone: 'America/Mexico_City', dateStyle: 'short', timeStyle: 'short' }).format(
    new Date(v),
  );

const UsersScreen: React.FC = () => {
  const navigate = useNavigate();
  const {
    users,
    currentUser,
    isLoading,
    formLoading,
    error,
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    setCurrentUser,
    clearCurrentUser,
    clearError,
  } = useUsers();

  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [sortField, setSortField] = useState<keyof User>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>(-1);
  const [toast, setToast] = useState<Toast | null>(null);

  // Consulta CM (fases 0 y 3): sesiones, baja por rotación y perfil. Sólo TI
  // (superadmin y admin); la API quita estos campos y rutas a los demás.
  const esTI = RoleService.isAdmin();
  const miId = StorageService.getUser()?.id ?? null;
  const [soloSinUso, setSoloSinUso] = useState(false);
  const [cerrando, setCerrando] = useState<User | null>(null);
  const [dandoBaja, setDandoBaja] = useState<User | null>(null);
  const [alcancesMetas, setAlcancesMetas] = useState<Record<string, AlcanceMetasMin[]>>({});

  const cargarAlcancesMetas = useCallback(async () => {
    try {
      const { alcances } = await MetasColaboradorService.listarAlcances();
      const porCuenta: Record<string, AlcanceMetasMin[]> = {};
      for (const a of alcances) {
        if (!porCuenta[a.userId]) porCuenta[a.userId] = [];
        porCuenta[a.userId].push({ tipo: a.tipo, nombre: a.nombre });
      }
      setAlcancesMetas(porCuenta);
    } catch {
      // Sin Metas (o sin permiso) la columna muestra sólo Consulta CM.
      setAlcancesMetas({});
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (esTI) void cargarAlcancesMetas();
  }, [esTI, cargarAlcancesMetas]);

  const visibles = useMemo(() => {
    if (!esTI || !soloSinUso) return users;
    const ahora = new Date();
    return users.filter((u) => sinUso(u, ahora));
  }, [users, esTI, soloSinUso]);
  const cuantosSinUso = useMemo(() => (esTI ? users.filter((u) => sinUso(u)).length : 0), [users, esTI]);

  useEffect(() => {
    if (error) {
      toast?.show({
        severity: 'error',
        summary: 'Error',
        detail: error,
        life: 3000,
      });
      clearError();
    }
  }, [error, toast, clearError]);

  const handleCreateUser = () => {
    setIsEditMode(false);
    clearCurrentUser();
    setSidebarVisible(true);
  };

  const handleEditUser = (user: User) => {
    setIsEditMode(true);
    setSidebarVisible(true);
    setCurrentUser(user);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setDeleteModalVisible(true);
  };

  const handleFormSubmit = async (data: CreateUserRequest | UpdateUserRequest) => {
    try {
      if (isEditMode && currentUser) {
        await updateUser(currentUser.id, data as UpdateUserRequest);
        toast?.show({
          severity: 'success',
          summary: 'Exito',
          detail: 'Usuario actualizado correctamente',
          life: 3000,
        });
      } else {
        await createUser(data as CreateUserRequest);
        toast?.show({
          severity: 'success',
          summary: 'Exito',
          detail: 'Usuario creado correctamente',
          life: 3000,
        });
      }
      setSidebarVisible(false);
      clearCurrentUser();
      fetchUsers();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleFormCancel = () => {
    setSidebarVisible(false);
    clearCurrentUser();
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete) {
      try {
        await deleteUser(userToDelete.id);
        toast?.show({
          severity: 'success',
          summary: 'Exito',
          detail: 'Usuario eliminado correctamente',
          life: 3000,
        });
        setDeleteModalVisible(false);
        setUserToDelete(null);
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setUserToDelete(null);
  };

  const getRoleSeverity = (role: string) => {
    switch (role) {
      case 'SUPERADMIN':
        return 'danger';
      case 'ADMIN':
        return 'danger';
      case 'MANAGER':
        return 'warning';
      case 'BRANCH_OFFICE':
        return 'info';
      case 'SELLER':
        return 'success';
      case 'WAREHOUSE_STAFF':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const formatDate = (date: Date | string) => {
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString();
    }
    return date.toLocaleDateString();
  };

  const actionBodyTemplate = (rowData: User) => {
    return (
      <div className="flex space-x-2">
        <button
          className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
          title="Editar usuario"
          onClick={() => handleEditUser(rowData)}
        >
          <i className="pi pi-pencil text-sm"></i>
        </button>
        {esTI && (
          <button
            className="p-2 text-amber-700 hover:text-amber-900 hover:bg-amber-50 rounded-full transition-colors"
            title="Cerrar sesión en todos sus equipos"
            aria-label="Cerrar sesión en todos sus equipos"
            onClick={() => setCerrando(rowData)}
          >
            <i className="pi pi-sign-out text-sm"></i>
          </button>
        )}
        {esTI && (() => {
          const motivo = rowData.id === miId
            ? 'No puede dar de baja su propia cuenta'
            : esCuentaCompartida(rowData.role)
              ? 'Las cuentas de tienda no se dan de baja por rotación'
              : null;
          return (
            <button
              className="p-2 text-red-700 hover:text-red-900 hover:bg-red-50 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
              title={motivo ?? 'Dar de baja (rotación)'}
              aria-label="Dar de baja (rotación)"
              disabled={!!motivo}
              onClick={() => setDandoBaja(rowData)}
            >
              <i className="pi pi-user-minus text-sm"></i>
            </button>
          );
        })()}
        <button
          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full transition-colors"
          title="Eliminar usuario"
          onClick={() => handleDeleteUser(rowData)}
        >
          <i className="pi pi-trash text-sm"></i>
        </button>
      </div>
    );
  };

  const perfilBodyTemplate = (rowData: User) =>
    rowData.perfil ? (
      <span>{rowData.perfil.nombre}</span>
    ) : (
      <span className="text-gray-400">{admitePerfil(rowData.role) ? 'Sin perfil' : '—'}</span>
    );

  const alcanceBodyTemplate = (rowData: User) => (
    <span className="text-sm">{textoAlcances(alcancesMetas[rowData.id] ?? [], rowData.alcancesNegocio ?? [])}</span>
  );

  const ultimoAccesoBodyTemplate = (rowData: User) => (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1">
        <span>{etiquetaUltimoAcceso(rowData.ultimoAcceso)}</span>
        {sinUso(rowData) && <Tag value="sin uso" severity="warning" />}
      </div>
      {rowData.sesionesValidasDesde && (
        <div
          className="text-xs text-gray-500"
          title={[rowData.sesionesCerradasPor, rowData.sesionesCerradasMotivo].filter(Boolean).join(' · ')}
        >
          Sesiones cerradas {fechaHoraMx(rowData.sesionesValidasDesde)}
        </div>
      )}
    </div>
  );

  const roleBodyTemplate = (rowData: User) => (
    <Tag 
      value={rowData.roleDisplay || rowData.role} 
      severity={getRoleSeverity(rowData.role)}
      className="capitalize"
    />
  );

  const dateBodyTemplate = (rowData: User) => (
    <span>{formatDate(rowData.createdAt)}</span>
  );

  const columns: TableColumn<User>[] = [
    {
      field: 'email',
      header: 'Correo electrónico',
      sortable: true,
      minWidth: '200px',
    },
    {
      field: 'name',
      header: 'Nombre',
      sortable: true,
      minWidth: '120px',
    },
    {
      field: 'branchOffice',
      header: 'Sucursal',
      sortable: true,
      minWidth: '120px',
    },
    {
      field: 'employeeNumber',
      header: 'No. empleado',
      sortable: true,
      minWidth: '110px',
    },
    {
      field: 'roleDisplay',
      header: 'Rol',
      sortable: true,
      body: roleBodyTemplate,
      minWidth: '140px',
    },
    ...(esTI
      ? ([
          {
            field: 'perfil',
            header: 'Perfil',
            body: perfilBodyTemplate,
            minWidth: '110px',
          },
          {
            field: 'alcancesNegocio',
            header: 'Alcance',
            body: alcanceBodyTemplate,
            minWidth: '180px',
          },
          {
            field: 'ultimoAcceso',
            header: 'Último acceso',
            body: ultimoAccesoBodyTemplate,
            minWidth: '140px',
          },
        ] as TableColumn<User>[])
      : []),
    {
      field: 'createdAt',
      header: 'Creado el',
      sortable: true,
      body: dateBodyTemplate,
      minWidth: '120px',
    },
    {
      field: 'id',
      header: 'Acciones',
      body: actionBodyTemplate,
      width: esTI ? '170px' : '120px',
    },
  ];

  return (
    <div className="space-y-6">
      <Toast ref={setToast} />

      {esTI && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            aria-pressed={soloSinUso}
            className={`rounded-full border border-solid px-3 py-1 text-sm transition-colors ${
              soloSinUso
                ? 'border-amber-500 bg-amber-100 text-amber-900'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
            onClick={() => setSoloSinUso((v) => !v)}
          >
            <i className="pi pi-clock mr-2 text-xs" />
            Sin uso en {DIAS_SIN_USO} días ({cuantosSinUso})
          </button>
          <span className="text-xs text-gray-500">
            Último acceso con un registro de hace {DIAS_SIN_USO} días o más. Las cuentas sin registro no cuentan: el
            registro empieza al desplegar.
          </span>
        </div>
      )}

      <TableWithHeader
        data={visibles}
        columns={columns}
        loading={isLoading}
        header={{
          title: 'Gestión de usuarios',
          subtitle: 'Gestiona los usuarios del sistema y sus permisos',
          actionButton: {
            icon: 'pi pi-plus',
            label: 'Crear usuario',
            onClick: handleCreateUser,
          },
          secondaryButton: {
            icon: 'pi pi-upload',
            label: 'Importar usuarios',
            onClick: () => navigate('/users/import'),
          },
        }}
        globalFilterValue={globalFilterValue}
        onGlobalFilterChange={setGlobalFilterValue}
        globalFilterPlaceholder="Buscar usuarios..."
        globalFilterFields={['email', 'userCode', 'roleDisplay', 'branchOffice', 'employeeNumber']}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={(field, order) => {
          setSortField(field);
          setSortOrder(order);
        }}
        emptyMessage="No se encontraron usuarios"
      />

      {/* Sidebar for Create/Edit Form */}
      <Sidebar
        visible={sidebarVisible}
        position="right"
        onHide={handleFormCancel}
        className="w-full md:w-6 lg:w-5 xl:w-4"
        style={{ width: '35rem' }}
        dismissable={false}
      >
        <div className="p-6">
          <UserForm
            user={isEditMode ? currentUser : undefined}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            isLoading={formLoading}
          />
        </div>
        {/* Alcance de metas (supervisor / regional): sólo cuentas Indicadores y
            sólo para administradores, que es a quien la API se lo permite. Va
            FUERA de UserForm: se guarda al instante contra otra ruta. */}
        {isEditMode && currentUser && currentUser.role === UserRole.INDICADORES && RoleService.isAdmin() && (
          <AlcanceMetasSection usuario={{ id: currentUser.id, email: currentUser.email }} />
        )}
        {/* Perfil y alcance de Consulta CM: TI, sólo en cuentas que lo admiten. */}
        {isEditMode && currentUser && esTI && admitePerfil(currentUser.role) && (
          <PerfilUsuarioSection usuario={currentUser} onGuardado={() => fetchUsers()} />
        )}
      </Sidebar>

      <CerrarSesionesDialog
        usuario={cerrando}
        onHide={() => setCerrando(null)}
        onHecho={(r) => {
          const quien = cerrando?.name || cerrando?.email || '';
          setCerrando(null);
          toast?.show({
            severity: 'success',
            summary: 'Sesiones cerradas',
            detail: `${quien}: tendrá que volver a iniciar sesión${r.socketsCerrados ? ` (${r.socketsCerrados} conexiones en vivo cerradas)` : ''}.`,
            life: 5000,
          });
          fetchUsers();
        }}
      />

      <BajaDialog
        usuario={dandoBaja}
        alcancesMetas={dandoBaja ? alcancesMetas[dandoBaja.id] ?? [] : []}
        esPropia={!!dandoBaja && dandoBaja.id === miId}
        onHide={() => setDandoBaja(null)}
        onHecho={() => {
          fetchUsers();
          void cargarAlcancesMetas();
        }}
        onAsignarResponsables={(tiendas) => {
          setDandoBaja(null);
          navigate(`/responsables?tiendas=${tiendasParam(tiendas)}`);
        }}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        user={userToDelete}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isLoading={isLoading}
      />
    </div>
  );
};

export default UsersScreen; 